import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The chrome every chart shares.
 *
 * `ChartFrame` owns responsive sizing, margins, the plot area and the pointer
 * layer. `Axes` owns gridlines, tick labels and the baseline. `ChartTooltip`
 * and `ChartLegend` own the rest of the furniture. A chart component on top of
 * these only draws its marks.
 *
 * That split is why the gridlines are the same weight, the axes the same
 * colour and the tooltips the same shape on every screen — there is one
 * implementation of each, not one per chart.
 *
 * Grid and axes are hairline, solid and recessive on purpose: dashed rules
 * read as data, and the data is the only thing allowed to be loud.
 */

const DEFAULT_MARGIN = { top: 12, right: 16, bottom: 26, left: 44 };

export const SERIES_COLORS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)'];

export function ChartFrame({
  height = 220,
  margin,
  onHover,
  onLeave,
  children,
  overlay,
  ariaLabel,
  className = '',
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const m = { ...DEFAULT_MARGIN, ...(margin || {}) };

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(element);
    setWidth(element.clientWidth);
    return () => observer.disconnect();
  }, []);

  const plotWidth = Math.max(0, width - m.left - m.right);
  const plotHeight = Math.max(0, height - m.top - m.bottom);

  const handleMove = useCallback(
    (event) => {
      if (!onHover) return;
      const rect = event.currentTarget.getBoundingClientRect();
      onHover({
        x: event.clientX - rect.left - m.left,
        y: event.clientY - rect.top - m.top,
        plotWidth,
        plotHeight,
        offsetLeft: m.left,
        offsetTop: m.top,
      });
    },
    [onHover, m.left, m.top, plotWidth, plotHeight]
  );

  return (
    <div ref={containerRef} className={`relative w-full ${className}`} style={{ height }}>
      {width > 0 && plotWidth > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={ariaLabel}
          onMouseMove={handleMove}
          onMouseLeave={onLeave}
          className="block"
        >
          <g transform={`translate(${m.left},${m.top})`}>
            {children({ plotWidth, plotHeight, offsetLeft: m.left, offsetTop: m.top })}
          </g>
        </svg>
      )}
      {overlay}
    </div>
  );
}

/**
 * Gridlines, tick labels and the baseline. Every chart passes its own scales;
 * none of them draws an axis itself.
 *
 * @param {Array<number>} yTicks    values in data space
 * @param {Function} y              value → pixel
 * @param {Array<number>} xTicks    values in data space (omit for banded charts)
 * @param {Function} x              value → pixel
 */
export function Axes({
  plotWidth,
  plotHeight,
  yTicks = [],
  y,
  formatY = String,
  xTicks = [],
  x,
  formatX = String,
  xBandLabels,
}) {
  return (
    <g aria-hidden="true">
      {yTicks.map((value) => (
        <g key={`y-${value}`}>
          <line
            x1={0}
            x2={plotWidth}
            y1={y(value)}
            y2={y(value)}
            stroke="var(--grid)"
            strokeWidth={1}
            shapeRendering="crispEdges"
          />
          <text
            x={-8}
            y={y(value)}
            textAnchor="end"
            dominantBaseline="middle"
            className="fill-[var(--ink-3)] text-[10.5px] tabular-nums"
          >
            {formatY(value)}
          </text>
        </g>
      ))}

      <line
        x1={0}
        x2={plotWidth}
        y1={plotHeight}
        y2={plotHeight}
        stroke="var(--axis)"
        strokeWidth={1}
        shapeRendering="crispEdges"
      />

      {xTicks.map((value, index) => (
        <text
          key={`x-${index}-${value}`}
          x={x(value)}
          y={plotHeight + 16}
          textAnchor={index === 0 ? 'start' : index === xTicks.length - 1 ? 'end' : 'middle'}
          className="fill-[var(--ink-3)] text-[10.5px] tabular-nums"
        >
          {formatX(value)}
        </text>
      ))}

      {xBandLabels?.map((band) => (
        <text
          key={`band-${band.label}`}
          x={band.x}
          y={plotHeight + 16}
          textAnchor="middle"
          className="fill-[var(--ink-3)] text-[10.5px]"
        >
          {band.label}
        </text>
      ))}
    </g>
  );
}

/**
 * The tooltip. Positioned inside the chart's own box and flipped near the right
 * edge, so it never spills out of the card.
 */
export function ChartTooltip({ x, y, containerWidth, title, rows }) {
  const flip = x > containerWidth - 150;

  return (
    <div
      className="pointer-events-none absolute z-10 min-w-[132px] rounded-lg border border-line bg-surface px-2.5 py-2 shadow-card"
      style={{
        left: flip ? undefined : x + 12,
        right: flip ? Math.max(4, containerWidth - x + 12) : undefined,
        top: Math.max(4, y),
      }}
      role="tooltip"
    >
      {title && <p className="mb-1 text-[11px] font-medium text-ink-3">{title}</p>}
      <ul className="space-y-0.5">
        {rows.map((row) => (
          <li key={row.label} className="flex items-center gap-2 text-[12.5px]">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: row.color }}
              aria-hidden="true"
            />
            <span className="flex-1 whitespace-nowrap text-ink-2">{row.label}</span>
            <span className="font-medium text-ink tabular-nums">{row.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A legend is always present for two or more series, and never for one — with
 * a single series the title already says what is plotted, and a one-swatch box
 * only restates it.
 *
 * The legend is also the relief for the light-mode contrast warning on the
 * third series colour: identity never rests on hue alone.
 */
export function ChartLegend({ series, className = '' }) {
  if (series.length < 2) return null;
  return (
    <ul className={`flex flex-wrap items-center gap-x-4 gap-y-1 ${className}`}>
      {series.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5 text-[12px] text-ink-2">
          <span
            className="h-0.5 w-3.5 rounded-full"
            style={{ background: item.color }}
            aria-hidden="true"
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}
